import { Loader } from '@fdha/web-ui-library';
import { Box, Chip, Divider, Typography, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import React, { useMemo } from 'react';
import {
  useGetMenuQuery,
  useListDishesQuery,
  useListMealKitsQuery,
} from '@fdha/graphql-api-foodops';

import { BasePage, PreviewPaper } from '../../../../components';
import { getOptions } from '../../../../utils';

const NUMBER_OF_RESULTS = 10000;

const MenuPreview = () => {
  const theme = useTheme();
  const { menuId } = useParams();

  const { data, loading } = useGetMenuQuery({
    variables: { id: menuId || '' },
    fetchPolicy: 'cache-and-network',
  });

  const menu = data?.menuById;

  const { data: dishesData, loading: loadingDishes } = useListDishesQuery({
    variables: { first: NUMBER_OF_RESULTS },
    fetchPolicy: 'cache-and-network',
  });

  const { data: mealKitsData, loading: loadingMealKits } = useListMealKitsQuery(
    {
      variables: { first: NUMBER_OF_RESULTS },
      fetchPolicy: 'cache-and-network',
    }
  );

  const isLoading = loading || loadingDishes || loadingMealKits;
  const dishOptions = useMemo(
    () => getOptions(dishesData?.dishes.edges),
    [dishesData?.dishes.edges]
  );

  const mealKitOptions = useMemo(
    () => getOptions(mealKitsData?.listMealKits.edges),
    [mealKitsData?.listMealKits.edges]
  );

  const preparedMealsValues = [
    {
      meal: 'Breakfast',
      dishes:
        dishOptions.filter((opt) => menu?.breakfast?.includes(opt.id)) || [],
    },
    {
      meal: 'Lunch',
      dishes: dishOptions.filter((opt) => menu?.lunch?.includes(opt.id)) || [],
    },
    {
      meal: 'Dinner',
      dishes: dishOptions.filter((opt) => menu?.dinner?.includes(opt.id)) || [],
    },
    {
      meal: 'Snacks',
      dishes: dishOptions.filter((opt) => menu?.snacks?.includes(opt.id)) || [],
    },
    {
      meal: 'Sides',
      dishes: dishOptions.filter((opt) => menu?.sides?.includes(opt.id)) || [],
    },
  ];

  const mealKitsValues = [
    {
      meal: 'Breakfast',
      dishes:
        mealKitOptions.filter((opt) =>
          menu?.breakfastMealKits?.includes(opt.id)
        ) || [],
    },
    {
      meal: 'Lunch',
      dishes:
        mealKitOptions.filter((opt) => menu?.lunchMealKits?.includes(opt.id)) ||
        [],
    },
    {
      meal: 'Dinner',
      dishes:
        mealKitOptions.filter((opt) =>
          menu?.dinnerMealKits?.includes(opt.id)
        ) || [],
    },
  ];
  const hasMealKitOptions = mealKitsValues.some(
    (value) => value.dishes.length > 0
  );

  const renderPreparedMealOption = (dish: string, id: string) => (
    <Chip
      key={id}
      label={dish}
      sx={{
        background: theme.palette.success.lightBg,
        border: `1px solid ${theme.palette.success.border}`,
      }}
    />
  );

  const renderMealKitOption = (dish: string, id: string) => (
    <Chip
      key={id}
      label={dish}
      sx={{
        background: theme.palette.warning.lightBg,
        border: `1px solid ${theme.palette.warning.border}`,
      }}
    />
  );

  return (
    <BasePage>
      <BasePage.BackButton to="/create" />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <PreviewPaper>
            <Typography padding={2} fontWeight="500">
              Menu Name
            </Typography>
            <Divider />
            <Typography padding={2}>{menu?.name}</Typography>
          </PreviewPaper>

          {/* Prepared meal options section */}
          <PreviewPaper sx={{ padding: 2, mb: 3 }}>
            <Typography variant="h6">Prepared meal options</Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />

            {preparedMealsValues.map((value) => (
              <Box key={value.meal} mb={3}>
                <Typography variant="subtitle1" fontWeight="500" mb={1}>
                  {value.meal}
                </Typography>
                <Box display="flex" flexWrap="wrap" columnGap={1} rowGap={1}>
                  {value.dishes.length ? (
                    value.dishes.map((dish) =>
                      renderPreparedMealOption(dish.label, dish.id)
                    )
                  ) : (
                    <Typography
                      variant="subtitle2"
                      fontStyle="italic"
                      color={theme.palette.text.secondary}
                    >
                      No options selected for {value.meal.toLowerCase()}.
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </PreviewPaper>

          {/* Meal kit options section */}
          {hasMealKitOptions && (
            <PreviewPaper sx={{ padding: 2 }}>
              <Typography variant="h6">Meals kit options</Typography>
              <Divider sx={{ mt: 1, mb: 2 }} />

              {mealKitsValues.map((value) => (
                <Box key={value.meal} mb={3}>
                  <Typography variant="subtitle1" fontWeight="500" mb={1}>
                    {value.meal}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" columnGap={1} rowGap={1}>
                    {value.dishes.length ? (
                      value.dishes.map((dish) =>
                        renderMealKitOption(dish.label, dish.id)
                      )
                    ) : (
                      <Typography
                        variant="subtitle2"
                        fontStyle="italic"
                        color={theme.palette.text.secondary}
                      >
                        No options selected for {value.meal.toLowerCase()}.
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </PreviewPaper>
          )}
        </>
      )}
    </BasePage>
  );
};

export default MenuPreview;
